/**
 * Backend Health Monitor
 *
 * Shared cross-amplifier health state. Prevents frontend amplifiers
 * (WebSocket reconnect storm, error-logging POST loop, performance-monitor
 * alert spam) from cascading when the backend is unreachable.
 *
 * Cross-tab consistency is provided via the `storage` event on
 * `__sys9_backend_health`. Stream C writes the same `window.__sys9_backend_health`
 * for observability dashboards.
 */

export interface BackendHealthState {
  isDown: boolean;
  consecutiveFailures: number;
  lastFailureAt: number | null;
  lastSuccessAt: number | null;
  /** Epoch ms until which the error logger should pause outbound POSTs. */
  pausedUntil: number | null;
}

type Listener = (state: BackendHealthState) => void;

const FAILURE_THRESHOLD = 3;
const FAILURE_WINDOW_MS = 60_000;
const ERROR_LOGGER_PAUSE_MS = 60_000;
const STORAGE_KEY = '__sys9_backend_health';
const WINDOW_KEY = '__sys9_backend_health';

const initialState = (): BackendHealthState => ({
  isDown: false,
  consecutiveFailures: 0,
  lastFailureAt: null,
  lastSuccessAt: null,
  pausedUntil: null,
});

function coerceNumber(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function readPersistedState(): BackendHealthState | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.consecutiveFailures === 'number'
    ) {
      return {
        isDown: Boolean(parsed.isDown),
        consecutiveFailures: parsed.consecutiveFailures,
        lastFailureAt: coerceNumber(parsed.lastFailureAt),
        lastSuccessAt: coerceNumber(parsed.lastSuccessAt),
        pausedUntil: coerceNumber(parsed.pausedUntil),
      };
    }
    return null;
  } catch {
    return null;
  }
}

function writePersistedState(state: BackendHealthState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage full or disabled; degrade silently. */
  }
}

/**
 * Dispatch listeners on a microtask so React renders never see a
 * synchronous setState mid-render. Listener errors must not break the
 * other listeners in the set.
 */
function notify(listeners: Set<Listener>, state: BackendHealthState): void {
  if (listeners.size === 0) return;
  queueMicrotask(() => {
    listeners.forEach(listener => {
      try {
        listener(state);
      } catch (err) {
        console.error('[backendHealth] listener threw:', err);
      }
    });
  });
}

class BackendHealthMonitor {
  private state: BackendHealthState = initialState();
  private listeners: Set<Listener> = new Set();
  private hydrated = false;

  constructor() {
    this.hydrate();
    this.installCrossTabSync();
  }

  private hydrate(): void {
    if (this.hydrated) return;
    this.hydrated = true;
    const persisted = readPersistedState();
    if (persisted) {
      const expiredPaused =
        persisted.pausedUntil !== null && persisted.pausedUntil <= Date.now();
      this.state = {
        ...persisted,
        pausedUntil: expiredPaused ? null : persisted.pausedUntil,
      };
    }
  }

  private installCrossTabSync(): void {
    if (typeof window === 'undefined') return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const persisted = readPersistedState();
      if (!persisted) return;
      this.state = { ...this.state, ...persisted };
      notify(this.listeners, this.state);
    };
    window.addEventListener('storage', onStorage);
  }

  private publish(next: BackendHealthState): void {
    this.state = next;
    if (typeof window !== 'undefined') {
      try {
        (window as unknown as Record<string, unknown>)[WINDOW_KEY] = next;
      } catch {
        /* CSPs may block reflective assignment; non-fatal. */
      }
    }
    writePersistedState(next);
    notify(this.listeners, next);
  }

  /** Resets the failure counter and clears any active error-logger pause. */
  recordSuccess(): void {
    this.publish({
      isDown: false,
      consecutiveFailures: 0,
      lastFailureAt: this.state.lastFailureAt,
      lastSuccessAt: Date.now(),
      pausedUntil: null,
    });
  }

  /**
   * Increments the rolling failure counter. When the counter crosses
   * `FAILURE_THRESHOLD` (3) within `FAILURE_WINDOW_MS` (60s), sets
   * `isDown` and arms a 60s `pausedUntil` to halt the error-logger POST
   * loop. `reason` is logged but never stored — avoids PII leak through
   * localStorage.
   */
  recordFailure(reason?: string): void {
    const now = Date.now();
    const withinWindow =
      this.state.lastFailureAt !== null &&
      now - this.state.lastFailureAt < FAILURE_WINDOW_MS;
    const consecutiveFailures = withinWindow
      ? this.state.consecutiveFailures + 1
      : 1;

    let isDown = this.state.isDown;
    let pausedUntil = this.state.pausedUntil;

    if (consecutiveFailures >= FAILURE_THRESHOLD) {
      isDown = true;
      pausedUntil = now + ERROR_LOGGER_PAUSE_MS;
    }

    if (reason && typeof console !== 'undefined') {
      console.warn(
        `[backendHealth] failure #${consecutiveFailures} (${reason})`
      );
    }

    this.publish({
      isDown,
      consecutiveFailures,
      lastFailureAt: now,
      lastSuccessAt: this.state.lastSuccessAt,
      pausedUntil,
    });
  }

  getState(): BackendHealthState {
    return { ...this.state };
  }

  /**
   * True while the error logger should skip its queue processor.
   * Auto-expires when `pausedUntil` has passed.
   */
  isErrorLoggerPaused(): boolean {
    if (this.state.pausedUntil === null) return false;
    if (Date.now() >= this.state.pausedUntil) return false;
    return true;
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function. Listeners
   * fire on a microtask — never synchronously inside another listener or
   * inside a React render.
   */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Test-only: reset to initial state. Not part of the public API. */
  __resetForTests(): void {
    this.state = initialState();
    this.listeners.clear();
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* Ignore. */
      }
    }
  }
}

export const backendHealth = new BackendHealthMonitor();

/** Constants exported so unit tests can exercise thresholds without
 * coupling to private internals. */
export const BACKEND_HEALTH_CONSTANTS = {
  FAILURE_THRESHOLD,
  FAILURE_WINDOW_MS,
  ERROR_LOGGER_PAUSE_MS,
  STORAGE_KEY,
  WINDOW_KEY,
} as const;

export type { Listener };
